import { Box, useToast } from "@chakra-ui/react";
import { useAppSelector } from "hooks/store/hooks";
import React, {
  Suspense,
  useEffect,
  useState,
  useTransition,
} from "react";
import lazy from "utils/lazyWithRetry";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import Topbar from "components/Topbar/index";
import DisplayArea from "./DisplayArea";
import AuthDisplayArea from "./AuthDisplayArea";
import CustomToast from "./CustomToast";

import {
  clearWholeLocalStorage,
  getAuthToken,
  getCurrentDeviceDetails,
  getUserDetails,
  removeForeignCurrentDevice,
  setCurrentDeviceDetails,
  setTempCurrentDeviceDetails,
} from "utils/local-storage";

import { selectColorThemeMode, selectlanguage } from "stores/ui/uiSlice";
import { markAuthStore } from "stores/auth/authSlice";
import { useDeviceDetection } from "utils/misc";
import PrintIPN from "pages/PrintIPN";
import QuickAccessBar from "./QuickAccessBar/QuickAccessBar";

// ✅ Lazy-loaded components
const SideDrawer = lazy(() => import("./SideDrawer"));
const MobileQuickAccessBar = lazy(() =>
  import("components/QuickAccessBar/MobileQuickAccessBar")
);
const AdswordTracker = lazy(() => import("components/Topbar/AdswordTracker"));

const Layout = ({ children }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition();
  const language = useAppSelector(selectlanguage);
  const { pathname } = useLocation();
  const [redirecting, setRedirecting] = useState(true);
  const isIndexPage =
    pathname === "/" ||
    pathname === "/forgotPassword" ||
    pathname === "/password" ||
    pathname.includes("/resetPassword") ||
    pathname.includes("/activation") ||
    pathname.includes("LoginWithOtp");

  const isActivationPage = pathname.includes("/activation");
  const colorThemeMode = useAppSelector(selectColorThemeMode);
  const userDetails = getUserDetails();
  // const [isDesktop] = useMediaQuery("(min-width: 768px)");
  const authToken = getAuthToken();
  const localCurrentDevice = getCurrentDeviceDetails();
  const dispatch = useDispatch();
  const { isDesktop } = useDeviceDetection();

  useEffect(() => {
    if(isActivationPage){
      setRedirecting(false);
      return;
    }
    if (authToken) {
      if (userDetails) {
        // Only trust the remembered device if it belongs to this account
        removeForeignCurrentDevice(userDetails.devices);
        const ownCurrentDevice = getCurrentDeviceDetails();
        if (ownCurrentDevice) {
          const { email_status, devices, email } = userDetails;
          dispatch(
            markAuthStore({
              userDetails: {
                email,
                devices,
                email_status,
              },
              currentDevice: ownCurrentDevice,
              family: ownCurrentDevice.family,
            })
          );
          if (isIndexPage) {
            startTransition(() => {
              navigate(
                ownCurrentDevice.family === 12
                  ? "/whatsappWebChat"
                  : "/dashboard",
                { replace: true }
              );
              setRedirecting(false);
            });
          } else {
            startTransition(() => {
              navigate(pathname, { replace: true });
              setRedirecting(false);
            });
          }
        } else {
          const { email_status, devices = [], email } = userDetails;
          dispatch(
            markAuthStore({
              userDetails: {
                email,
                devices,
                email_status,
              },
              currentDevice: devices[0] ?? null,
              family: devices[0]?.family ?? 1,
            })
          );
          if (devices?.length === 0) {
            startTransition(() => {
              navigate("/registerNewDevice", { replace: true });
              setRedirecting(false);
            });
          } else {
            startTransition(() => {
              navigate(pathname, { replace: true });
              setRedirecting(false);
            });
            setCurrentDeviceDetails(devices[0]);
            setTempCurrentDeviceDetails(devices[0]);
          }
        }
      } else {
        clearWholeLocalStorage();
        toast({
          position: "top-right",
          status: "warning",
          render: () => (
            <CustomToast bgColor={"red"} message={"Session Expired..."} />
          ),
        });
        startTransition(() => {
          navigate("/", { replace: true });
          setRedirecting(false);
        });
      }
    } else {
      if (isIndexPage) {
        clearWholeLocalStorage();
        startTransition(() => {
          navigate(pathname, { replace: true });
          setRedirecting(false);
        });
      } else {
        clearWholeLocalStorage();
        startTransition(() => {
          navigate("/", { replace: true });
          setRedirecting(false);
        });
      }
    }
  }, [navigate, pathname]);
   
  if (pathname.includes("printIPN")) {
  return (
    <Suspense fallback={<></>}>
      <PrintIPN />
    </Suspense>
  );
}
  if (redirecting) return <AuthDisplayArea />;
  return (
    <Suspense fallback={<></>}>
      <Box
        display={"flex"}
        minH="100vh"
        h="100%"
        bg={`${colorThemeMode}_background`}
        position={"relative"}
        dir={language === "ar" || language === "il" ? "rtl" : "ltr"}
      >
        <>
          {!isIndexPage && (
            <>
              <SideDrawer />
              <Box w="full" bg={`${colorThemeMode}_tertiary`}>
                <>
                  <Suspense fallback={<></>}>
                    <AdswordTracker />
                  </Suspense>
                  <Topbar />
                  {isDesktop === 'computer' ? <QuickAccessBar /> : <MobileQuickAccessBar />}
                  {/* {isDesktop ? <QuickAccessBar /> : shouldRenderHeader && (
                    <MobileQuickAccessBar />
                  )} */}
                  {/* {isDesktop ? <QuickAccessBar /> : <MobileQuickAccessBar />} */}
                  <DisplayArea>{children}</DisplayArea>
                </>
              </Box>
            </>
          )}{" "}
          {isIndexPage && (
            <Box w="full" bg={`${colorThemeMode}_tertiary`} minH={"100%"}>
              <AuthDisplayArea>{children}</AuthDisplayArea>
            </Box>
          )}
          {/* <Suspense fallback={<></>}>
            <ChatSupport />
          </Suspense>
          <Suspense fallback={<></>}>
            <GtmTagManager />
          </Suspense>
          <Suspense fallback={<></>}>
            <CookiesBar />
          </Suspense> */}
        </>
      </Box>
    </Suspense>
  );
};

export default Layout;
